import { type AccessState, trialDaysLeft } from '@lingoping/core';
import { formatDate } from './dates.js';

export type SubscriptionTone = 'ok' | 'warn' | 'bad' | 'neutral';

export interface SubscriptionSummary {
  /** One short line: "Subscribed — next payment 8 December 2026". */
  text: string;
  tone: SubscriptionTone;
  /** Whether a subscription exists whose card can be changed in Paddle's portal. */
  hasSubscription: boolean;
  /** Whether it can still be cancelled (it exists and is not already cancelled). */
  cancellable: boolean;
}

/** A cancellation is on its way: Paddle's status is canceled, or one is scheduled for later. */
export function isCancelled(access: AccessState): boolean {
  const sub = access.subscription;
  return !!sub && (sub.status === 'canceled' || sub.cancel_at !== null);
}

/** What Settings > Subscription says about where the learner stands. */
export function summarizeAccess(access: AccessState): SubscriptionSummary {
  const sub = access.subscription;
  const none = { hasSubscription: false, cancellable: false };

  if (access.state === 'locked') {
    if (access.reason === 'payment_failed') {
      return { text: 'Payment failed', tone: 'bad', hasSubscription: true, cancellable: true };
    }
    return {
      text: access.reason === 'trial_ended' ? 'Trial ended' : 'Subscription ended',
      tone: 'bad',
      ...none,
    };
  }

  if (sub && access.state === 'active') {
    if (isCancelled(access)) {
      const end = formatDate(sub.cancel_at ?? sub.current_period_end ?? access.until);
      return {
        text: end ? `Cancelled — access until ${end}` : 'Cancelled',
        tone: 'warn',
        hasSubscription: true,
        cancellable: false,
      };
    }
    if (sub.status === 'past_due') {
      return { text: 'Payment failed', tone: 'bad', hasSubscription: true, cancellable: true };
    }
    const next = formatDate(sub.current_period_end);
    return {
      text: next ? `Subscribed — next payment ${next}` : 'Subscribed',
      tone: 'ok',
      hasSubscription: true,
      cancellable: true,
    };
  }

  if (access.state === 'grant') {
    const end = formatDate(access.until);
    return { text: end ? `Free access until ${end}` : 'Free access', tone: 'ok', ...none };
  }

  const end = formatDate(access.trial_ends_at);
  return { text: end ? `Trial until ${end}` : 'Trial', tone: 'neutral', ...none };
}

/** The top bar's trial line: "Trial: 9 days left", or "Trial ends today" on the last day. */
export function trialLineText(access: AccessState, now = new Date()): string | null {
  const days = trialDaysLeft(access, now);
  if (days === null) return null;
  if (days <= 1) return 'Trial ends today';
  return `Trial: ${days} days left`;
}
